/**
 * The undo plugin for simcision.
 * Creates undo states depending on the actions dispatched in the store.
 * Each action is treated as undoable, and the differences between before and after are saved
 * to the protocoll to be able to undo and redo many actions.
 */

import { orderBy, sortBy, cloneDeep, isEqual } from "lodash-es";
import { acceptHMRUpdate, defineStore } from "pinia";
import { applyPatch } from "fast-json-patch";
import type { SetOptional } from "type-fest";
import { computed, markRaw, reactive } from "vue";
import { i18n } from "@/i18n";
import { showMessage } from "@core/ui/alert/alert";
import CreateUndoEntryWorker from "./createUndoEntry.worker.js?worker";

import type { Store } from "pinia";
import type { Operation } from "fast-json-patch";
import type { Ref } from "vue";

const IGNORED_STORES: string[] = [];
const IGNORED_ACTIONS = {
  "model/project": ["showSaveDialog", "save"],
  "model/media": ["removeNewFlags"]
} as Record<string, string[]>;

interface UndoStoreState {
  canUndo: boolean;
  canRedo: boolean;
  enabled: boolean;
  shouldIgnoreNextAction: boolean;
  stacks: { [key: string]: UndoStack };
  stackPastLengths: { [key: string]: number };
  unsubscribeStoreListeners: Record<string, (() => void)[]>;
}

export const useUndo = defineStore("undo", {
  state: (): UndoStoreState => ({
    canUndo: false,
    canRedo: false,
    enabled: true,

    shouldIgnoreNextAction: false,

    stacks: {},
    stackPastLengths: {},

    unsubscribeStoreListeners: {} as Record<string, (() => void)[]>
  }),

  getters: {
    getStack() {
      return (stackName = "model") => (
        this.stacks[stackName] as UndoStack | undefined
      );
    }
  },

  actions: {
    /**
     * Add a store to the undo stack.
     */
    addToStack(itemStore: Store, stackName: string) {
      if (!this.stacks[stackName]) {
        this.createStack(stackName);
      }

      const stack = this.stacks[stackName];

      stack.stores[itemStore.$id] = itemStore;

      // set the initial state
      stack.previousState[itemStore.$id] = JSON.stringify(itemStore.$state);

      /**
       * On any store ACTION, save the current root STATE.
       * Also, save the action, to group any done within that action.
       * Called before the action.
       * @param action The action beeing recorded
       * @param action.name Name of the action
       * @param action.store Instance of the store the action happened in.
       * @param action.args Array of parameters the action received
       * @param action.after Hook to clean up after the action happened
       * @param action.onError Hook to clean up when the action failed
       */
      const unsubscribe = itemStore.$onAction(({ name, after, args, store, onError }) => {
        if (this.enabled === false) {
          return;
        }

        // check if the action should be undoable
        const maybeUndoOptions = args?.[args.length - 1] as { _undoable?: boolean } | undefined;
        if (maybeUndoOptions && typeof maybeUndoOptions === "object" && "_undoable" in maybeUndoOptions && maybeUndoOptions._undoable === false) {
          console.log(
            `%cNOT _undoable: ${store.$id}.${name}`,
            "color: #666; background-color: #fee; font-size: 10px; display: block; padding: 0.2em;"
          );
          return;
        }

        if (isIgnoringAction(store.$id, name)) return;

        if (isIgnoringStore(store.$id)) return;

        if (this.shouldIgnoreNextAction) {
          this.ignoreNextAction(false);
          return;
        }

        const groupId = stack.startGroup(name, store.$id);

        after(onActionDone(groupId, store, name, args));
        onError(onActionDone(groupId, store, name, args));
      });
      if (!this.unsubscribeStoreListeners[stackName]) {
        this.unsubscribeStoreListeners[stackName] = [];
      }
      this.unsubscribeStoreListeners[stackName].push(unsubscribe);

      const onActionDone = (
        groupId: number,
        store: Store,
        actionName: string,
        args: unknown[]
      ) => {
        return () => {
          // leave only the module affected by the current mutation
          const newState = JSON.stringify(itemStore.$state);
          stack.logAction(newState, store, actionName, args);
          stack.endGroup(groupId);
        };
      };
    },

    /**
     * Create a new undo stack.
     */
    createStack(stackName: string) {
      const stack = new UndoStack();
      this.stacks[stackName] = markRaw(stack);

      stack.onUndoUpdate(() => {
        this.updateStackData(stackName);
      });
    },

    ignoreNextAction(value: boolean) {
      this.shouldIgnoreNextAction = value;
    },

    /**
     * Start a new group of actions.
     */
    startGroup(stackName: string, actionName: string) {
      if (stackName == null) throw new Error("stackName can't be empty");
      if (!this.stacks[stackName]) return;
      return this.stacks[stackName].startGroup(actionName);
    },

    /**
     * End a group of actions.
     */
    endGroup(stackName: string, groupId?: number) {
      if (!this.stacks[stackName]) return;
      this.stacks[stackName].endGroup(groupId);
    },

    disable() {
      this.enabled = false;
    },
    enable() {
      this.enabled = true;
    },

    /**
     * Undo the last action of the past list
     */
    undo(stackName: string) {
      if (!stackName) throw new Error("We need the name of the undo stack!");

      const stack = this.stacks[stackName];

      if (stack.groups.length > 0) {
        showMessage(i18n.t("errors.noUndoDuringAction"));
        console.error("Is in action, can't undo.", stack.groups);
        return;
      }

      if (!stack.canUndo) return;

      stack.applyUndo();

      this.updateStackData(stackName);
    },

    undoAll(stackName: string) {
      if (!stackName) throw new Error("We need the name of the undo stack!");

      const stack = this.stacks[stackName];

      if (stack.groups.length > 0) {
        showMessage(i18n.t("errors.noUndoDuringAction"));
        return;
      }

      while (stack.canUndo) {
        stack.applyUndo();
      }

      this.updateStackData(stackName);
    },

    /**
     * Redo the first action group on the redo stack
     */
    redo(stackName: string) {
      if (!stackName) throw new Error("We need the name of the undo stack!");

      const stack = this.stacks[stackName];

      if (stack.groups.length > 0) {
        showMessage(i18n.t("errors.noRedoDuringAction"));
        return;
      }

      if (!stack.canRedo) {
        console.warn("stack can't redo");
        return;
      }

      stack.applyRedo();

      this.updateStackData(stackName);
    },

    redoAll(stackName: string) {
      if (!stackName) throw new Error("We need the name of the undo stack!");

      const stack = this.stacks[stackName];

      if (stack.groups.length > 0) {
        showMessage(i18n.t("errors.noRedoDuringAction"));
        return;
      }

      while (stack.canRedo) {
        stack.applyRedo();
      }

      this.updateStackData(stackName);
    },

    updateStackData(stackName: string) {
      const stack = this.stacks[stackName];
      if (stackName == "model") {
        this.canRedo = stack.canRedo;
        this.canUndo = stack.canUndo;
      }
      this.stackPastLengths[stackName] = Object.values(stack.past).length;
    },

    reset() {
      // unsubscribe all listeners
      for (const stackName in this.unsubscribeStoreListeners) {
        this.unsubscribeStoreListeners[stackName].forEach((unsubscribe) => unsubscribe());
      }
      this.unsubscribeStoreListeners = {};

      // destroy all stacks
      for (const stackId in this.stacks) {
        this.stacks[stackId].destroy();
      }
      this.$reset();
    }
  }
});

export type UndoEntry = {
  id: number;
  storeId: string; // The id of the store (e.g. "model/elements")
  action: string;
  group: number;
  arguments: unknown[];
  createdAt: number;

  reverses?: Operation[];
  changes?: Operation[];

  combine?: boolean; // internal flag: combine undo with last?
};

type Group = {
  id: number;
  action: string;
  storeId?: string;
  // args: any[];
};

class UndoStack {
  past: Record<number, UndoEntry>;
  future: UndoEntry[];

  action: string;
  groupId: number;
  undoId: number;
  previousState: { [storeId: string]: string }; // stringified state per store

  stores: Record<string, Store>;
  groups: Group[];

  undoEntryWorker: Worker;
  _undoUpdates: ((stack: UndoStack) => void)[];

  nextPossibleUndo: Ref<UndoEntry | null>;
  lastActionId: Ref<number | null>;

  constructor() {
    this.past = reactive({});
    this.future = reactive([]);

    this.action = "";
    this.groupId = 0;
    this.undoId = 0;
    this.previousState = {};

    this.stores = {};

    this.undoEntryWorker = this.setupWorker();

    this.groups = [];

    this._undoUpdates = [];

    this.nextPossibleUndo = computed(() => {
      const keys = Object.keys(this.past).map((key) => parseInt(key));
      const key = orderBy(keys, undefined, "desc")[0];
      if (key == null) return null;
      return this.past[key];
    });

    this.lastActionId = computed(() => {
      return this.nextPossibleUndo.value?.id ?? null;
    });
  }

  get canUndo() {
    return Object.values(this.past).length > 0;
  }

  get canRedo() {
    return this.future.length > 0;
  }

  applyUndo() {
    let currentUndo = this.nextPossibleUndo.value;

    if (currentUndo != null) {
      const currentGroup = currentUndo.group;

      do {
        const storeId = currentUndo.storeId;
        const store = this.stores[storeId];

        const stateToChange = store.$state;

        const reverses = cloneDeep(currentUndo.reverses);

        this.removeLastUndo();

        if (reverses) {
          console.log(
            "%c[UNDO]",
            "background-color: red; padding: 0.2em;",
            currentUndo.storeId,
            currentUndo.action
          );

          // revert the changes saved in an undo
          applyPatch(stateToChange, reverses);
          store.$state = stateToChange;

          // save the state as the new base for the next undos
          this.previousState[storeId] = JSON.stringify(stateToChange);
        }

        // move undo to redo list
        this.future.push(currentUndo);

        // get the next undo already - if it's in the same group,
        // undo it too.
        currentUndo = this.nextPossibleUndo.value;
        if (currentUndo == null) break;
      } while (currentUndo.group === currentGroup);
    }

    // when there are no undos left
    if (currentUndo == null) {
      // delete all entries
      for (const key in this.past) {
        delete this.past[key];
      }
    }
  }

  applyRedo() {
    let nextRedoIndex = this.future.length - 1;

    // redo all undos of a group
    const currentGroup = this.future[nextRedoIndex].group;

    do {
      const currentUndo = this.future.pop();

      if (!currentUndo) continue;

      const storeId = currentUndo.storeId;
      const store = this.stores[storeId];
      const stateToChange = store.$state;

      const changes = cloneDeep(currentUndo.changes);

      if (changes) {
        console.log(
          "%c[REDO]",
          "background-color: limegreen; padding: 0.2em;",
          currentUndo.storeId,
          currentUndo.action
        );

        // revert the changes saved in an undo
        applyPatch(stateToChange, changes);
        store.$state = stateToChange;

        // save the state as the new base for the next undos
        this.previousState[storeId] = JSON.stringify(stateToChange);
      }

      const pastId = currentUndo.id;

      // this.undoId = Math.max(this.undoId, currentUndo.id);

      // move undo to past list
      this.past[pastId] = currentUndo;

      nextRedoIndex--;
      if (nextRedoIndex < 0) break;
    } while (this.future[nextRedoIndex].group === currentGroup);
  }

  /**
   * Create an undo worker that compares the changes done to the current state
   * and saves only the changes.
   */
  setupWorker() {
    const undoEntryWorker = new CreateUndoEntryWorker();
    console.log("created new worker", undoEntryWorker);

    /**
     * called when the an undo entry is processed:
     */
    undoEntryWorker.onmessage = (event) => {
      let { nextUndo } = event.data;

      if (nextUndo.changes == null) {
        if (!nextUndo.combine) {
          delete this.past[nextUndo.id];
        }
        return false;
      }

      // ==== mutation combining ====
      // if that past slot is already taken, combine the undos.
      const lastUndo = this.past[nextUndo.id];
      if (lastUndo != null && lastUndo.changes != null) {
        nextUndo = combineLastUndo(nextUndo, this, nextUndo.id);
      }

      // save entry in past stack.
      this.past[nextUndo.id] = nextUndo;

      this._undoUpdates.forEach((update) => update(this));
    };

    return undoEntryWorker;
  }

  /**
   * Create a new group
   */
  startGroup(actionName: string, storeId?: string) {
    this.groupId++;

    this.groups.unshift({
      id: this.groupId,
      action: actionName,
      storeId
    });

    // are there redo actions? delete them if there are.
    if (this.future.length > 0) {
      this.future = [];
    }

    return this.groupId;
  }

  endGroup(groupId?: number) {
    if (groupId == null) {
      return this.groups.shift();
    }
    for (let groupIndex = 0; groupIndex < this.groups.length; groupIndex++) {
      if (this.groups[groupIndex].id === groupId) {
        this.groups.splice(groupIndex, 1);
      }
    }
  }

  /**
   * Save an action in the past protocol of the current stack:
   */
  logAction(
    changedState: string,
    store: Store,
    actionName: string,
    args: unknown[] = []
  ) {
    const createdAt = Date.now();

    const mainGroup = this.groups[this.groups.length - 1];

    const storeId = store.$id;

    const nextUndo = {
      id: undefined as number | undefined,
      storeId,
      group: mainGroup.id,
      arguments: cloneDeep(args),
      action: actionName,
      combine: false,
      createdAt: createdAt
    };

    // ==== undo grouping ====

    const lastUndo = this.nextPossibleUndo.value;
    if (
      lastUndo &&
      isLastActionSame(
        {
          group: nextUndo.group,
          action: nextUndo.action,
          storeId: nextUndo.storeId,
          arguments: nextUndo.arguments,
          createdAt: nextUndo.createdAt
        },
        lastUndo
      )
    ) {
      // If the undo action is the same as the last one, set the id to the last undo id.
      // Grouping merges all changes into ONE undo. This makes sense for changes
      // that consist of a lot of small actions, like text editing or element movements.
      nextUndo.combine = true;

      nextUndo.id = lastUndo.id;
    }
    // Not the same? save as different undo:
    else {
      // increment the undo id
      const undoId = this.undoId;

      // save undo as placeholder in past stack.
      nextUndo.id = undoId;
      this.past[undoId] = nextUndo as UndoEntry;

      this.undoId++;
    }

    // ==== transmit to worker ====

    const isLastOpenStoreGroup =
      this.groups.reduce((openGroups, group) => {
        if (group.storeId === storeId) openGroups++;
        return openGroups;
      }, 0) === 1;

    // only handle changes if this is the last open undo group of the store:
    if (isLastOpenStoreGroup) {
      // collect all data for the worker
      const dataForWorker = {
        nextUndo,
        changedState,
        previousModuleState: this.previousState[storeId]
      };

      // Update the previous state.
      this.previousState[storeId] = changedState;

      // send the data to the worker to check the differences and create the undo entry.
      try {
        this.undoEntryWorker.postMessage(dataForWorker);
      } catch (error) {
        console.error(error, dataForWorker);
      }
    }
  }

  removeLastUndo() {
    const keys = Object.keys(this.past).map((key) => parseInt(key));
    const highestKey = orderBy(keys, undefined, "desc")[0];
    delete this.past[highestKey];
    // this.undoId = highestKey;
  }

  clear() {
    this.past = {};
    this.future = [];
  }

  destroy() {
    this.undoEntryWorker.terminate();
  }

  onUndoUpdate(callback: () => void) {
    this._undoUpdates.push(callback);
  }
}

//
// *** Functions ***
//

/**
 * Create a joined before state for a new undo
 * @param undo The new undo
 * @param stack The stack the undos are in
 */
function combineLastUndo(undo: UndoEntry, stack: UndoStack, index: number) {
  // remove the last undo from the undo stack:
  const lastUndo = stack.past[index];

  // combine the properties to undo:
  const newReverses = mergePatch(undo.reverses || [], lastUndo.reverses || []);
  const newChanges = mergePatch(lastUndo.changes || [], undo.changes || []);

  return {
    ...undo,
    changes: newChanges,
    reverses: newReverses
  };
}

/**
 * Merge two jsonpatch operations
 */
function mergePatch(oldPatch: Operation[], newPatch: Operation[]) {
  const merged = JSON.parse(JSON.stringify(oldPatch));
  for (const operation of newPatch) {
    let newOp = true;

    // check if the operation existed in the old patch
    for (const oldOperation of merged) {
      // if it exists, replace it.
      if (
        oldOperation.op === operation.op &&
        oldOperation.path === operation.path
      ) {
        if ("value" in operation) {
          oldOperation.value = operation.value;
        }
        newOp = false;
        break;
      }
    }

    // if it doesn't exist, add it.
    if (newOp) {
      merged.push(operation);
    }
  }
  return merged;
}

/**
 * Checks if one change was the same type as the action
 * done before. Useful for grouping mutations like movement
 * or text input.
 * @param undo
 * @param lastUndo
 */
function isLastActionSame(
  undo: SetOptional<UndoEntry, "id">,
  lastUndo: SetOptional<UndoEntry, "id">
) {
  if (lastUndo == null) return false;
  if (lastUndo.storeId !== undo.storeId) return false;
  if (lastUndo.action !== undo.action) return false;
  if (undo.arguments == null) return false;

  // Don't group mutations that had a pause between
  // them. Time intervals should seperate mutations.
  if (undo.createdAt - lastUndo.createdAt > 500) return false;

  const args = undo.arguments;
  const oldArguments = lastUndo.arguments;

  // if there's no payload, we can't make sure it's the same action.
  if (args == null || oldArguments == null) return false;

  // only handle actions with the same amount of arguments
  if (args.length !== oldArguments.length) {
    return false;
  }

  // only handle actions that belong to the same object
  if (
    args?.[0] == null ||
    typeof args[0] != "object" ||
    !("id" in args[0]) ||
    args?.[0]?.id == null ||
    oldArguments[0] == null ||
    typeof oldArguments[0] != "object" ||
    !("id" in oldArguments[0]) ||
    oldArguments[0]?.id !== args[0].id
  ) {
    return false;
  }

  // ONLY GROUP ACTIONS WITH THE SAME PAYLOAD KEYS
  if (
    !isEqual(sortBy(Object.keys(args[0])), sortBy(Object.keys(oldArguments[0])))
  ) {
    return false;
  }

  return true;
}

function isIgnoringAction(storeId: string, actionName: string) {
  return (
    IGNORED_ACTIONS[storeId] && IGNORED_ACTIONS[storeId].includes(actionName)
  );
}

function isIgnoringStore(storeId: string) {
  return IGNORED_STORES.includes(storeId);
}

if (import.meta.hot) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  import.meta.hot.accept(acceptHMRUpdate(useUndo as any, import.meta.hot));
}
